import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors.
// Only force a hard redirect to /login when the user is actually unauthenticated
// (auth probes or any 401 while we still expect to be authenticated). Avoid
// reloading on stale/post-unmount 401s of accessory endpoints (avatars, etc.)
// and when we are already on /login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Probes that confirm session validity — these failing means the token is bad.
      const isAuthProbe = url.includes('/auth/me') || url.includes('/auth/login');
      const onLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
      if (isAuthProbe && !onLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For 401s on accessory/stale requests we just reject; the calling code
      // may handle it (or ignore it) without forcing a full page reload.
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`),
  assignKPIs: (userId, kpiIds) => api.post(`/users/${userId}/assign-kpis`, { kpi_ids: kpiIds }),
  getAssignedKPIs: (userId) => api.get(`/users/${userId}/assigned-kpis`),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (userId, newPassword, forceChange = true) => 
    api.post(`/users/${userId}/reset-password`, { new_password: newPassword, force_change: forceChange }),
  uploadAvatar: (userId, formData) => api.post(`/users/${userId}/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAvatar: (userId) => api.delete(`/users/${userId}/avatar`),
  getPermissions: (userId) => api.get(`/users/${userId}/permissions`),
  updatePermissions: (userId, permissions) => api.put(`/users/${userId}/permissions`, { permissions }),
};

// KPIs API
export const kpisAPI = {
  getAll: (params) => api.get('/kpis', { params }),
  getById: (id) => api.get(`/kpis/${id}`),
  create: (data) => api.post('/kpis', data),
  update: (id, data) => api.put(`/kpis/${id}`, data),
  getAreas: () => api.get('/kpis/areas/list'),
  bulkAction: (data) => api.post('/kpis/bulk-action', data),
  // Periodicity & Buckets
  getPeriodicityOptions: () => api.get('/periodicity-options'),
  getBuckets: (kpiId, year) => api.get(`/buckets/${kpiId}`, { params: { year } }),
  getCurrentBucket: (kpiId, year) => api.get(`/buckets/${kpiId}/current`, { params: { year } }),
  // KPI Order (per user)
  getOrder: () => api.get('/kpis/order'),
  updateOrder: (orderedKpiIds) => api.put('/kpis/order', { ordered_kpi_ids: orderedKpiIds }),
  resetOrder: () => api.delete('/kpis/order'),
  // Soft delete & trash
  remove: (id) => api.delete(`/kpis/${id}`),
  restore: (id) => api.post(`/kpis/${id}/restore`),
  getTrash: () => api.get('/kpis/trash'),
};

// Periods API
export const periodsAPI = {
  getAll: (params) => api.get('/periods', { params }),
  getCurrent: () => api.get('/periods/current'),
  getCaptureContext: (kpiId) => api.get(`/periods/capture-context/${kpiId}`),
  generate: (year, config = null) => api.post(`/periods/generate/${year}`, config),
  regenerate: (year, config = null) => api.post(`/periods/regenerate/${year}`, config),
  delete: (year) => api.delete(`/periods/${year}`),
  getBimesters: (year) => api.get(`/periods/bimesters/${year}`),
  getQuarters: (year) => api.get(`/periods/quarters/${year}`),
  // Calendar config
  getConfig: (year) => api.get(`/calendar-config/${year}`),
  saveConfig: (config) => api.post('/calendar-config', config),
  deleteConfig: (year) => api.delete(`/calendar-config/${year}`),
};

// Goals API
export const goalsAPI = {
  getAll: (params) => api.get('/goals', { params }),
  getById: (id) => api.get(`/goals/${id}`),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
};

// Results API
export const resultsAPI = {
  getAll: (params) => api.get('/results', { params }),
  create: (data) => api.post('/results', data),
  update: (id, data) => api.put(`/results/${id}`, data),
};

// Analysis API
export const analysisAPI = {
  getKPIAnalysis: (kpiId, params) => api.get(`/analysis/kpi/${kpiId}`, { params }),
  getDashboard: (params) => api.get('/analysis/dashboard', { params }),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Teams API
export const teamsAPI = {
  getAll: () => api.get('/teams'),
  getById: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  updateMembers: (id, memberIds) => api.put(`/teams/${id}/members`, { member_ids: memberIds }),
  updateKpis: (id, kpiIds) => api.put(`/teams/${id}/kpis`, { kpi_ids: kpiIds }),
};

// Comments API
export const commentsAPI = {
  getAll: (params) => api.get('/comments', { params }),
  getCount: (params) => api.get('/comments/count', { params }),
  create: (data) => api.post('/comments', data),
  getSummary: (kpiId) => api.get(`/kpis/${kpiId}/comments-summary`),
  toggleCancel: (id) => api.post(`/comments/${id}/toggle-cancel`),
};

// ============================================
// HELP DESK API
// ============================================

// Help Desk Categories API
export const helpdeskCategoriesAPI = {
  getAll: () => api.get('/helpdesk/categories'),
  create: (data) => api.post('/helpdesk/categories', data),
  update: (id, data) => api.put(`/helpdesk/categories/${id}`, data),
  delete: (id) => api.delete(`/helpdesk/categories/${id}`),
};

// Knowledge Base — dynamic categories
export const kbCategoriesAPI = {
  getAll: () => api.get('/helpdesk/kb-categories'),
  create: (data) => api.post('/helpdesk/kb-categories', data),
  update: (id, data) => api.put(`/helpdesk/kb-categories/${id}`, data),
  delete: (id) => api.delete(`/helpdesk/kb-categories/${id}`),
};

// Help Desk Tickets API
export const helpdeskTicketsAPI = {
  getAll: (params) => api.get('/helpdesk/tickets', { params }),
  getById: (id) => api.get(`/helpdesk/tickets/${id}`),
  create: (data) => api.post('/helpdesk/tickets', data),
  update: (id, data) => api.put(`/helpdesk/tickets/${id}`, data),
  getComments: (id) => api.get(`/helpdesk/tickets/${id}/comments`),
  addComment: (id, data) => api.post(`/helpdesk/tickets/${id}/comments`, data),
  getHistory: (id) => api.get(`/helpdesk/tickets/${id}/history`),
  // Attachments
  getAttachments: (id) => api.get(`/helpdesk/tickets/${id}/attachments`),
  uploadAttachments: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/helpdesk/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Bulk delete (Admin Master only)
  bulkDeleteTickets: (ticketIds) => api.post('/helpdesk/tickets/bulk-delete', { ticket_ids: ticketIds }),
  // Bulk assign agent to closed tickets (Admin only)
  bulkAssignClosed: (ticketIds, assignedTo) => api.post('/helpdesk/tickets/bulk-assign-closed', { ticket_ids: ticketIds, assigned_to: assignedTo }),
  // CSAT resolution feedback (requester only)
  rateCsat: (id, data) => api.post(`/helpdesk/tickets/${id}/csat`, data),
};

// Help Desk Attachments API
export const helpdeskAttachmentsAPI = {
  download: (attachmentId) => api.get(`/helpdesk/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  delete: (attachmentId) => api.delete(`/helpdesk/attachments/${attachmentId}`),
  // Get preview URL for images (returns the URL, not the data)
  getPreviewUrl: (attachmentId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    return `${baseUrl}/api/helpdesk/attachments/${attachmentId}/preview?token=${encodeURIComponent(token || '')}`;
  },
};

// Help Desk Articles API (Knowledge Base)
export const helpdeskArticlesAPI = {
  getAll: (params) => api.get('/helpdesk/articles', { params }),
  getById: (id) => api.get(`/helpdesk/articles/${id}`),
  create: (data) => api.post('/helpdesk/articles', data),
  update: (id, data) => api.put(`/helpdesk/articles/${id}`, data),
  delete: (id) => api.delete(`/helpdesk/articles/${id}`),
  getCategories: () => api.get('/helpdesk/articles/categories'),
  uploadAttachment: (articleId, formData) => api.post(`/helpdesk/articles/${articleId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAttachment: (articleId, attachmentId) => api.delete(`/helpdesk/articles/${articleId}/attachments/${attachmentId}`),
  getAttachmentUrl: (articleId, attachmentId) => `${api.defaults.baseURL}/helpdesk/articles/${articleId}/attachments/${attachmentId}`,
};

// Help Desk SLA Config API
export const helpdeskSLAAPI = {
  getConfig: () => api.get('/helpdesk/sla-config'),
  updateConfig: (data) => api.put('/helpdesk/sla-config', data),
};

// Help Desk Metrics API
export const helpdeskMetricsAPI = {
  getMetrics: () => api.get('/helpdesk/metrics'),
};

// Help Desk Reports API
export const helpdeskReportsAPI = {
  getPeriodReport: (params) => api.get('/helpdesk/reports/period', { params }),
  getCsatReport: (params) => api.get('/helpdesk/reports/csat', { params }),
  getAgents: () => api.get('/helpdesk/reports/agents'),
};

// Help Desk Agents API
export const helpdeskAgentsAPI = {
  getAll: () => api.get('/helpdesk/agents'),
  toggleAgent: (userId) => api.put(`/helpdesk/agents/${userId}/toggle`),
  getUserRole: () => api.get('/helpdesk/user-role'),
};

// Help Desk Config API (IT Team settings)
export const helpdeskConfigAPI = {
  getITTeam: () => api.get('/helpdesk/config/it-team'),
  setITTeam: (teamId) => api.put('/helpdesk/config/it-team', { team_id: teamId }),
  getStatusTransitions: () => api.get('/helpdesk/status-transitions'),
  getDateFilters: () => api.get('/helpdesk/date-filters'),
  resetTickets: () => api.delete('/helpdesk/admin/reset-tickets'),
};

// Help Desk Email Logs API
export const helpdeskEmailLogsAPI = {
  getAll: (params) => api.get('/helpdesk/email-logs', { params }),
  getFailed: (params) => api.get('/helpdesk/email-logs/failed', { params }),
  getByTicket: (ticketId) => api.get(`/helpdesk/tickets/${ticketId}/email-logs`),
};

// Help Desk Email Config API
export const helpdeskEmailConfigAPI = {
  getConfig: () => api.get('/helpdesk/email-config'),
  updateConfig: (data) => api.put('/helpdesk/email-config', { params: data }),
};

// Menu Permissions API
export const menuPermissionsAPI = {
  getAll: () => api.get('/menu-permissions'),
  getForRole: (role) => api.get(`/menu-permissions/for-role/${role}`),
  update: (updates) => api.put('/menu-permissions', updates),
  reset: () => api.post('/menu-permissions/reset'),
  checkAccess: (menuKey, subKey) => api.get(`/menu-permissions/check/${menuKey}`, { params: { sub_key: subKey } }),
};

// Orgchart API
export const orgchartAPI = {
  getAll: () => api.get('/orgchart/nodes'),
  create: (data) => api.post('/orgchart/nodes', data),
  update: (nodeId, data) => api.put(`/orgchart/nodes/${nodeId}`, data),
  delete: (nodeId) => api.delete(`/orgchart/nodes/${nodeId}`),
  uploadAttachment: (nodeId, formData) => api.post(`/orgchart/nodes/${nodeId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (nodeId, storagePath) => api.delete(`/orgchart/nodes/${nodeId}/attachments/${storagePath}`),
};

// Colaboradores (Capital Humano) API
export const colaboradoresAPI = {
  getAll: (params) => api.get('/colaboradores', { params }),
  getById: (id) => api.get(`/colaboradores/${id}`),
  create: (data) => api.post('/colaboradores', data),
  update: (id, data) => api.put(`/colaboradores/${id}`, data),
  baja: (id, data) => api.post(`/colaboradores/${id}/baja`, data),
  reingreso: (id, data) => api.post(`/colaboradores/${id}/reingreso`, data),
  downloadTemplate: () => api.get('/colaboradores/export/template', { responseType: 'blob' }),
  exportFull: () => api.get('/colaboradores/export/full', { responseType: 'blob' }),
  importFile: (formData) => api.post('/colaboradores/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const vigilanciaAPI = {
  // Gafetes
  getGafetes: () => api.get('/vigilancia/gafetes'),
  updateGafetes: (data) => api.put('/vigilancia/gafetes', data),
  // Visitas
  getVisitas: (params) => api.get('/vigilancia/visitas', { params }),
  getAreas: () => api.get('/vigilancia/catalogos/areas'),
  createVisita: (data) => api.post('/vigilancia/visitas', data),
  confirmarLlegada: (id) => api.post(`/vigilancia/visitas/${id}/confirmar-llegada`),
  registrarSalidaVisita: (id) => api.post(`/vigilancia/visitas/${id}/registrar-salida`),
  // Herramentales
  getVales: (params) => api.get('/vigilancia/herramentales', { params }),
  getVale: (id) => api.get(`/vigilancia/herramentales/${id}`),
  createVale: (data) => api.post('/vigilancia/herramentales', data),
  registrarSalidaVale: (id, formData) => api.post(`/vigilancia/herramentales/${id}/registrar-salida`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  registrarRegresoVale: (id, formData) => api.post(`/vigilancia/herramentales/${id}/registrar-regreso`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  cerrarVale: (id) => api.post(`/vigilancia/herramentales/${id}/cerrar`),
};

export default api;
